import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, MenuItem, TextField } from "@mui/material";
import { Edit2 } from "react-feather";
import { withSuperAdminAuth } from "../../../components/GetAuth";
import PageHeader from "../../../components/PageHeader";
import { formatGBP, formatGBP2 } from "../../../components/Features";
import billingStyles from "../../../components/billing.module.css";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";
import { Kpi, saGet, saPut, StatusChip, TableSkeleton, Toast, useToast } from "../shared";
import styles from "../superadmin.module.css";

// Add-on catalogue: price (default £16.99/month), name, description, availability and adoption
const AddonList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, showMessage, closeToast] = useToast();

  const { websiteData } = useWebsite();
  useSiteBranding("Add-ons & pricing", websiteData);

  const load = () => {
    saGet("addons").then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") setRows(response.data.data);
    }).catch(() => setLoading(false));
  };

  useEffect(load, []);

  const save = () => {
    setSaving(true);
    saPut(`addons/${edit.id}`, { name: edit.name, description: edit.description, monthly_price: edit.monthly_price, status: Number(edit.status), sort_order: Number(edit.sort_order) || 0 }).then((response) => {
      setSaving(false);
      showMessage(response.data.message, response.data.status === "SUCCESS" ? "success" : "error");
      if (response.data.status === "SUCCESS") { setEdit(null); load(); }
    }).catch(() => { setSaving(false); showMessage("Could not save the add-on.", "error"); });
  };

  const totalMrr = rows.reduce((s, r) => s + Number(r.mrr || 0), 0);
  const mostPopular = [...rows].sort((a, b) => b.active_companies - a.active_companies)[0];
  const original = edit ? rows.find((r) => r.id === edit.id) : null;
  const priceChanged = original && Number(original.monthly_price) !== Number(edit?.monthly_price);

  return (<div className={`${styles.Page}`}>
    <PageHeader title="Add-ons & Pricing" trail={[{ label: "Billing", to: "/super-admin/invoices" }]} />

    {loading && <TableSkeleton kpis={3} rows={9} />}

    {!loading && (<>
      <div className={`${styles.KpiGrid}`}>
        <Kpi label="Add-on value / month" value={formatGBP(totalMrr)} sub="All enabled add-ons, incl. trials" />
        <Kpi label="Most popular" value={mostPopular?.active_companies > 0 ? mostPopular.name : "—"} sub={mostPopular?.active_companies > 0 ? `${mostPopular.active_companies} companies` : "No add-ons in use yet"} />
        <Kpi label="Basic plan price" value={<Link to={"/super-admin/settings"} className={`${styles.RowLink}`}>Billing settings →</Link>} sub="Basic price, trial and grace period" />
      </div>

      <div className={`${styles.TableWrap}`}>
        <table className={`${styles.Table}`}>
          <thead>
            <tr><th>Add-on</th><th>Key</th><th className={`${styles.Right}`}>Price / month</th><th className={`${styles.Right}`}>Companies</th><th className={`${styles.Right}`}>MRR</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (<tr key={r.id}>
              <td><strong>{r.name}</strong><span className={`${styles.SubText}`}>{r.description}</span></td>
              <td><code style={{ fontSize: 12 }}>{r.key}</code></td>
              <td className={`${styles.Right}`}>{formatGBP2(r.monthly_price)}</td>
              <td className={`${styles.Right}`}><Link to={`/super-admin/companies?addon=${r.key}`} className={`${styles.RowLink}`}>{r.active_companies}</Link></td>
              <td className={`${styles.Right}`}>{formatGBP2(r.mrr)}</td>
              <td>{Number(r.status) === 1 ? <StatusChip status="active" label="Available" /> : <StatusChip status="void" label="Hidden" />}</td>
              <td><div className={`${styles.Actions}`}><button type="button" className={`${styles.ActionBU}`} onClick={() => setEdit({ ...r })}><Edit2 />Edit</button></div></td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </>)}

    <Dialog open={!!edit} onClose={() => !saving && setEdit(null)} maxWidth="sm" fullWidth>
      <DialogTitle>Edit {original?.name}</DialogTitle>
      <DialogContent>
        <TextField label="Name" value={edit?.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} fullWidth size="small" margin="dense" />
        <TextField label="Description" value={edit?.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} fullWidth size="small" margin="dense" multiline minRows={2} />
        <TextField label="Monthly price" type="number" value={edit?.monthly_price ?? ""} onChange={(e) => setEdit({ ...edit, monthly_price: e.target.value })} fullWidth size="small" margin="dense" inputProps={{ min: 0, step: "0.01" }} InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }} />
        <TextField label="Availability" select value={String(edit?.status ?? 1)} onChange={(e) => setEdit({ ...edit, status: e.target.value })} fullWidth size="small" margin="dense" helperText="Hidden add-ons can't be turned on by companies. Companies already using one keep it.">
          <MenuItem value="1">Available to companies</MenuItem>
          <MenuItem value="0">Hidden</MenuItem>
        </TextField>
        <TextField label="Sort order" type="number" value={edit?.sort_order ?? 0} onChange={(e) => setEdit({ ...edit, sort_order: e.target.value })} fullWidth size="small" margin="dense" />
        {priceChanged && <p className={`${styles.Example}`}>New price <strong>{formatGBP2(edit.monthly_price)}</strong> applies from each company's next invoice ({original.active_companies} companies use this add-on). Invoices already issued keep the old price.</p>}
      </DialogContent>
      <DialogActions sx={{ padding: "0 24px 20px" }}>
        <button type="button" className={`${billingStyles.SecondaryBU}`} onClick={() => setEdit(null)} disabled={saving}>Cancel</button>
        <button type="button" className={`${billingStyles.PrimaryBU}`} onClick={save} disabled={saving || !edit?.name || edit?.monthly_price === ""}>{saving ? "Saving…" : "Save"}</button>
      </DialogActions>
    </Dialog>
    <Toast toast={toast} onClose={closeToast} />
  </div>);
}

export default withSuperAdminAuth(AddonList);
